import React, { useEffect, useState, useCallback } from "react";
import {
  Row,
  Col,
  Typography,
  Button,
  Table,
  Space,
  Tag,
  Modal,
  Input,
  Select,
  Popconfirm,
  notification,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import i18n from "i18n";
import _get from "lodash/get";
import {
  getMessageTemplatesRequest,
  deleteMessageTemplateRequest,
  setTemplateFilters,
  clearTemplateFilters,
  messageTemplateSelectors,
} from "providers/MessageTemplateProvider/slice";
import MessageTemplateModal from "./MessageTemplateModal";
import "./styles.less";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const MessageTemplates = () => {
  const dispatch = useDispatch();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const templates = useSelector(messageTemplateSelectors.selectAll);
  const loading = useSelector((state) => state.messageTemplate.loading);
  const deleting = useSelector((state) => state.messageTemplate.deleting);
  const totalDocs = useSelector((state) => state.messageTemplate.totalDocs);
  const page = useSelector((state) => state.messageTemplate.page);
  const limit = useSelector((state) => state.messageTemplate.limit);
  const filters = useSelector((state) => state.messageTemplate.filters);

  useEffect(() => {
    dispatch(getMessageTemplatesRequest({ page: 1, limit: 10 }));

    // Inject CSS to fix popconfirm button layout
    const style = document.createElement('style');
    style.textContent = `
      .ant-popover-buttons {
        display: flex !important;
        flex-direction: row !important;
        gap: 8px !important;
        justify-content: flex-end !important;
      }
      
      .ant-popover-buttons .ant-btn {
        margin-left: 8px !important;
        margin-bottom: 0 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [dispatch]);

  const handleTableChange = useCallback(
    (pagination, filters, sorter) => {
      dispatch(
        getMessageTemplatesRequest({
          page: pagination.current,
          limit: pagination.pageSize,
          ...filters,
        })
      );
    },
    [dispatch]
  );

  const handleSearch = useCallback(
    (value) => {
      const newFilters = { ...filters, searchQuery: value };
      dispatch(setTemplateFilters(newFilters));
      dispatch(getMessageTemplatesRequest({ page: 1, limit, ...newFilters }));
    },
    [dispatch, filters, limit]
  );

  const handleCategoryFilter = useCallback(
    (category) => {
      const newFilters = { ...filters, category };
      dispatch(setTemplateFilters(newFilters));
      dispatch(getMessageTemplatesRequest({ page: 1, limit, ...newFilters }));
    },
    [dispatch, filters, limit]
  );

  const handleStatusFilter = useCallback(
    (isActive) => {
      const newFilters = { ...filters, isActive };
      dispatch(setTemplateFilters(newFilters));
      dispatch(getMessageTemplatesRequest({ page: 1, limit, ...newFilters }));
    },
    [dispatch, filters, limit]
  );

  const handleCreate = useCallback(() => {
    setEditingTemplate(null);
    setModalVisible(true);
  }, []);

  const handleEdit = useCallback((template) => {
    setEditingTemplate(template);
    setModalVisible(true);
  }, []);

  const handleDelete = useCallback(
    (id) => {
      dispatch(deleteMessageTemplateRequest({ id }));
    },
    [dispatch]
  );

  const handlePreview = useCallback((template) => {
    setPreviewTemplate(template);
    setPreviewVisible(true);
  }, []);

  const handleModalCancel = useCallback(() => {
    setModalVisible(false);
    setEditingTemplate(null);
  }, []);

  const handleModalSuccess = useCallback(() => {
    setModalVisible(false);
    setEditingTemplate(null);
    dispatch(getMessageTemplatesRequest({ page, limit, ...filters }));
  }, [dispatch, page, limit, filters]);

  const renderPreviewContent = (content) => {
    if (!content) return "";
    
    // Replace placeholders with example values for preview
    const placeholders = {
      "{customer_name}": "Nguyễn Văn A",
      "{product_name}": "iPhone 15 Pro",
      "{order_id}": "DH123456",
      "{price}": "25.000.000 VNĐ",
      "{date}": new Date().toLocaleDateString("vi-VN"),
    };

    let previewContent = content;
    Object.entries(placeholders).forEach(([placeholder, value]) => {
      previewContent = previewContent.replace(new RegExp(placeholder, "g"), value);
    });

    return previewContent;
  };

  const columns = [
    {
      title: "Tiêu đề",
      dataIndex: "title",
      key: "title",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Danh mục",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: (category) => (
        <Tag color="blue">{category || "Chưa phân loại"}</Tag>
      ),
    },
    {
      title: "Nội dung",
      dataIndex: "content",
      key: "content",
      width: 300,
      ellipsis: true,
      render: (content, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {record.image && (
            <img
              src={typeof record.image === 'string' ? record.image : record.image.url}
              alt="Template"
              style={{
                width: 40,
                height: 30,
                objectFit: 'cover',
                borderRadius: 4,
                border: '1px solid #d9d9d9'
              }}
            />
          )}
          <div style={{ maxWidth: record.image ? 190 : 250, overflow: "hidden", textOverflow: "ellipsis" }}>
            {content}
          </div>
        </div>
      ),
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      width: 150,
      render: (tags) => (
        <div>
          {tags?.slice(0, 2).map((tag, index) => (
            <Tag key={index} size="small">
              {tag}
            </Tag>
          ))}
          {tags?.length > 2 && <Tag size="small">+{tags.length - 2}</Tag>}
        </div>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Hoạt động" : "Tắt"}
        </Tag>
      ),
    },
    {
      title: "Ngày tạo",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => new Date(date).toLocaleDateString("vi-VN"),
    },
    {
      title: "Hành động",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
            title="Xem trước"
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Chỉnh sửa"
          />
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa template này?"
            onConfirm={() => handleDelete(record._id)}
            okText="Có"
            cancelText="Không"
            placement="topRight"
            okButtonProps={{ 
              size: "small",
              style: { marginLeft: '8px' }
            }}
            cancelButtonProps={{ 
              size: "small"
            }}
            overlayInnerStyle={{
              minWidth: '200px'
            }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={deleting}
              title="Xóa"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="message-templates-page">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4}>Quản lý Template Tin nhắn</Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            size="large"
          >
            Tạo Template
          </Button>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Search
            placeholder="Tìm kiếm template..."
            allowClear
            onSearch={handleSearch}
            style={{ width: "100%" }}
          />
        </Col>
        <Col span={6}>
          <Select
            placeholder="Chọn danh mục"
            allowClear
            style={{ width: "100%" }}
            onChange={handleCategoryFilter}
            value={filters.category}
          >
            <Option value="greeting">Chào hỏi</Option>
            <Option value="order">Đơn hàng</Option>
            <Option value="support">Hỗ trợ</Option>
            <Option value="promotion">Khuyến mãi</Option>
            <Option value="notification">Thông báo</Option>
          </Select>
        </Col>
        <Col span={6}>
          <Select
            placeholder="Trạng thái"
            allowClear
            style={{ width: "100%" }}
            onChange={handleStatusFilter}
            value={filters.isActive}
          >
            <Option value={true}>Hoạt động</Option>
            <Option value={false}>Tắt</Option>
          </Select>
        </Col>
        <Col span={4}>
          <Button
            onClick={() => {
              dispatch(clearTemplateFilters());
              dispatch(getMessageTemplatesRequest({ page: 1, limit: 10 }));
            }}
          >
            Xóa bộ lọc
          </Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={templates}
        loading={loading}
        rowKey="_id"
        pagination={{
          current: page,
          pageSize: limit,
          total: totalDocs,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} của ${total} template`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
      />

      <MessageTemplateModal
        visible={modalVisible}
        onCancel={handleModalCancel}
        onSuccess={handleModalSuccess}
        editingTemplate={editingTemplate}
      />

      <Modal
        title="Xem trước Template"
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Đóng
          </Button>,
        ]}
        width={600}
      >
        {previewTemplate && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>Tiêu đề:</strong> {previewTemplate.title}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Danh mục:</strong>{" "}
              <Tag color="blue">{previewTemplate.category || "Chưa phân loại"}</Tag>
            </div>
            {previewTemplate.tags && previewTemplate.tags.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <strong>Tags:</strong>{" "}
                {previewTemplate.tags.map((tag, index) => (
                  <Tag key={index}>{tag}</Tag>
                ))}
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <strong>Nội dung gốc:</strong>
              <div
                style={{
                  background: "#f5f5f5",
                  padding: 12,
                  borderRadius: 4,
                  marginTop: 8,
                  whiteSpace: "pre-wrap",
                }}
              >
                {previewTemplate.content}
              </div>
            </div>
            {previewTemplate.image && (
              <div style={{ marginBottom: 16 }}>
                <strong>Hình ảnh:</strong>
                <div style={{ marginTop: 8 }}>
                  <img
                    src={typeof previewTemplate.image === 'string' ? previewTemplate.image : previewTemplate.image.url}
                    alt="Template"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 300,
                      borderRadius: 4,
                      border: '1px solid #d9d9d9',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              </div>
            )}
            <div>
              <strong>Nội dung sau khi thay thế:</strong>
              <div
                style={{
                  background: "#e6f7ff",
                  padding: 12,
                  borderRadius: 4,
                  marginTop: 8,
                  whiteSpace: "pre-wrap",
                }}
              >
                {renderPreviewContent(previewTemplate.content)}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MessageTemplates;